import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { publicApi, useBranches } from './publicApi';
import { todayIso } from '../shopTime';
import { SERVICE_IMAGES } from '../catalogImages';
import './spa.css';

const money = (n) => new Intl.NumberFormat('th-TH').format(Number(n || 0));
export const image = (number) => `/spa-images/saanti-${String(number).padStart(3, '0')}.webp`;

const PAGES = [
  { path: '/spa', label: 'Home', exact: true },
  { path: '/spa/story', label: 'Our story' },
  { path: '/spa/massage', label: 'About massage' },
  { path: '/spa/services', label: 'Services' },
  { path: '/spa/facilities', label: 'Facilities' },
  { path: '/spa/contact', label: 'Contact' },
];

// The source folder contains adjacent frames from the same photo/video shoot.
// Taking the first six therefore repeated the storefront several times even
// though the filenames were different. Curate one distinct scene per tile.
const GALLERY_IMAGES = [
  image(1),   // hot-stone ritual
  image(18),  // reception
  image(35),  // Thai massage room
  image(94),  // foot massage room
  image(124), // therapist at work
  image(150), // closing tea
];

export { SERVICE_IMAGES };

export const SERVICE_COPY = {
  SAANTI_THAI: 'Rhythmic pressure, assisted stretching and attentive hands restore ease throughout the body.',
  DEEP_TISSUE: 'Focused pressure works into tired muscles and the places where the city tends to stay with you.',
  NECK_SHOULDER: 'A concentrated treatment for the neck, shoulders and upper back when time is short.',
  AROMA_OIL: 'Warm aromatic oil and long, flowing strokes create a slower, softer kind of restoration.',
  AROMA_COUPLE: 'A shared aromatherapy ritual prepared for two guests in the quiet of a private room.',
  HOT_STONE: 'Smooth heated stones warm the body while measured pressure releases deep-set tension.',
  FOOT: 'A grounding foot ritual that refreshes tired legs and settles the whole body.',
  VP5: 'Thai balm massage followed by a focused foot treatment for complete head-to-toe relief.',
};

const GUEST_REVIEWS = [
  {
    author: 'Emily',
    quote: 'The most relaxing massage of my life!! Love the minimal interior design, the service was incredibly professional. I would come back everyday if I could.',
  },
  {
    author: 'Nano',
    quote: 'A nice place to relax, would definitely recommend if you’re around here.',
  },
  {
    author: 'Yoyo',
    quote: 'Lovely place in Song Wat—super clean, cozy, and the staff are all amazing. Every therapist is skilled and friendly. Feels like home every time I visit.',
  },
  {
    author: 'Kikkchy',
    quote: 'Such a nice place! The service was really good and the staff were sooo nice, which made me feel super welcome the whole time. I left feeling totally relaxed, like all my stress melted away. Also, the crackers were delicious!! Didn’t expect that little treat but it was sooo good. Definitely coming back again!',
  },
  {
    author: 'Ploynapus',
    quote: 'สบายมากค่ะ บรรยากาศร้านเหมือนอยู่บ้านเลยค่ะ ชอบสถานที่มากๆ ขนมตอนนวดเสร็จอร่อย ได้ลูกประคบกลับบ้านอีก ชอบบบบบบบ',
  },
  {
    author: 'Kunyakorn',
    quote: 'Hidden gem for true relaxation. From the moment I stepped through the door, it felt like I’d entered a little sanctuary tucked away from the chaos. The ambience was warm, inviting, and thoughtfully designed—soft lighting, gentle aromas, and peaceful music that instantly put me at ease. Highly recommended!!',
  },
  {
    author: 'Voranuch',
    quote: 'It’s a nice place for relaxing. Good price and good massage, staff are very professional. Highly recommended!!',
  },
  {
    author: 'Siyapat',
    quote: 'พนักงานนวดฝีมือดีมากกก สถานที่สงบ สะอาด สวยงาม เหมาะกับการมาผ่อนคลายจากความวุ่นวายใจกลางเมือง',
  },
  {
    author: 'Ying',
    quote: 'ร้านสวย อยากนวดพอดี ดีเกินคาด หมอนวดนวดสบายๆ บรรยากาศอบอุ่น พนักงานให้คำแนะนำดีมาก จะมาใช้บริการอีกค่ะ',
  },
  {
    author: 'Kavee',
    quote: 'The place is cozy and clean. I had two hours of Thai massage—so relaxing. The therapist was good and professional. Worth the money; I’d love to come back.',
  },
  {
    author: 'Ken',
    quote: 'ศานติ (SAANTI) วันนี้มาเดินเล่นแถวทรงวาด เดินจนเมื่อย เลยลองหาร้านนวดดู แล้วบังเอิญมาเจอร้านนี้ เป็นร้านนวดไทยเปิดใหม่ บรรยากาศดีมาก เลยพาคุณแฟนเข้ามาใช้บริการ ร้านตกแต่งสวย สะอาด และดูมืออาชีพ พนักงานน่ารัก เป็นกันเองสุดๆ รู้สึกผ่อนคลายตั้งแต่ก้าวแรกที่เดินเข้าไป ใครกำลังมองหาร้านนวดดีๆ สบายตัว แนะนำที่นี่เลยครับ ประทับใจแน่นอน',
  },
  {
    author: 'Patchara',
    quote: 'สถานที่สะอาด ตกแต่งสวย พนักงานบริการดี หมอนวดนวดดีมากๆๆๆ',
  },
];

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
};

export function SpaNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <nav className="spa-nav">
      <div className="spa-utility">
        <div className="wrap">
          <span>Traditional Thai wellness in Bangkok</span>
          <span>Open daily · 10:00–22:00</span>
        </div>
      </div>

      <div className="wrap spa-nav-main">
        <Link to="/spa" className="spa-brand" aria-label="SAANTI Massage home">
          <img src="/spa-images/saanti-logo-gold-transparent.png" alt="SAANTI Thai Massage" />
        </Link>

        <button
          type="button"
          className="spa-menu-toggle"
          aria-label="Toggle navigation"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          <i />
          <i />
        </button>

        <div className={`spa-nav-links${open ? ' open' : ''}`}>
          {PAGES.map((page) => (
            <Link
              key={page.path}
              to={page.path}
              className={
                page.exact
                  ? pathname === page.path ? 'on' : ''
                  : pathname.startsWith(page.path) ? 'on' : ''
              }
              onClick={() => setOpen(false)}
            >
              {page.label}
            </Link>
          ))}
          <button className="spa-cta compact" onClick={() => navigate('/spa/book')}>
            Book now
          </button>
        </div>
      </div>
    </nav>
  );
}

export function SpaFooter() {
  const navigate = useNavigate();
  const { branches } = useBranches();

  return (
    <footer className="spa-footer">
      <div className="wrap">
        <div className="spa-footer-brand">
          <img src="/spa-images/saanti-logo-gold-transparent.png" alt="SAANTI Thai Massage" />
          <p>Quiet rooms, attentive hands and Thai tradition in the heart of Bangkok.</p>
        </div>

        <div className="cols">
          <div>
            <h4>Explore</h4>
            <Link to="/spa/story">Our story</Link>
            <Link to="/spa/massage">About massage</Link>
            <Link to="/spa/services">Services</Link>
            <Link to="/spa/facilities">Facilities</Link>
            <Link to="/spa/contact">Contact us</Link>
            <button type="button" onClick={() => navigate('/spa/book')}>Book a treatment</button>
          </div>
          <div>
            <h4>Visit</h4>
            {(branches || []).map((branch) => (
              <p key={branch.code}>{branch.name} · {branch.nameTh}</p>
            ))}
            <p>981 Song Wat Road<br />Samphanthawong, Bangkok 10100</p>
          </div>
          <div>
            <h4>Contact</h4>
            <a href="mailto:sales.saanti@gmail.com">sales.saanti@gmail.com</a>
            <a href="tel:+66955923236">095 592 3236</a>
            <p>Open daily {branches?.[0]?.hours || '10:00 - 22:00'}</p>
            <p>Walk-ins are always welcome.</p>
            <div className="spa-socials" aria-label="SAANTI social media">
              <a
                href="https://www.instagram.com/saanti.thaimassage/"
                target="_blank"
                rel="noreferrer"
                aria-label="SAANTI on Instagram"
              >
                Instagram
              </a>
              <a
                href="https://www.facebook.com/people/Saanti-Thai-Massage-Songwat/61574191362794/"
                target="_blank"
                rel="noreferrer"
                aria-label="SAANTI on Facebook"
              >
                Facebook
              </a>
              <a
                href="https://www.tiktok.com/@saantithaimassagesongwat"
                target="_blank"
                rel="noreferrer"
                aria-label="SAANTI on TikTok"
              >
                TikTok
              </a>
            </div>
          </div>
        </div>
        <div className="base">© {todayIso().slice(0, 4)} SAANTI Massage. All rights reserved.</div>
      </div>
    </footer>
  );
}

export default function SpaHome() {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const { branches, error: branchError } = useBranches();
  const [services, setServices] = useState(null);
  const [error, setError] = useState(null);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [reviewTransition, setReviewTransition] = useState(null);
  const reviewTimerRef = useRef(null);

  const firstBranch = branches?.[0]?.code;
  useEffect(() => {
    if (!firstBranch) return;
    publicApi.services(firstBranch)
      .then((data) => setServices(data.services))
      .catch((requestError) => setError(requestError.message));
  }, [firstBranch]);

  const loaded = Boolean(services || error || branchError);
  useEffect(() => {
    if (hash && loaded) scrollToSection(hash.slice(1));
  }, [hash, loaded]);

  const book = () => navigate(firstBranch ? `/spa/book?branch=${firstBranch}` : '/spa/book');
  const galleryImages = GALLERY_IMAGES.slice(0, 6);
  const reviewLengthClass = (review) => review.quote.length > 300
    ? 'long'
    : review.quote.length > 180 ? 'medium' : '';
  const reviewIsThai = (review) => /[\u0E00-\u0E7F]/.test(review.quote);

  useEffect(() => () => window.clearTimeout(reviewTimerRef.current), []);

  const moveReview = (direction) => {
    if (reviewTransition) return;
    const nextIndex =
      (reviewIndex + direction + GUEST_REVIEWS.length) % GUEST_REVIEWS.length;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setReviewIndex(nextIndex);
      return;
    }

    setReviewTransition({ from: reviewIndex, to: nextIndex, direction });
    window.clearTimeout(reviewTimerRef.current);
    reviewTimerRef.current = window.setTimeout(() => {
      setReviewIndex(nextIndex);
      setReviewTransition(null);
    }, 680);
  };

  return (
    <div className="spa">
      <SpaNav />

      <header className="spa-hero">
        <img src={image(106)} alt="SAANTI therapist preparing a traditional Thai massage room" />
        <div className="spa-hero-shade" />
        <div className="wrap spa-hero-content">
          <div className="spa-kicker">The art of Thai restoration</div>
          <h1>Come back<br />to yourself.</h1>
          <p>
            Traditional Thai massage, thoughtful rituals and a quiet place to
            breathe—just steps from Bangkok&apos;s historic Song Wat Road.
          </p>
          <div className="spa-hero-actions">
            <button className="spa-cta light" onClick={book}>Reserve a time</button>
            <Link to="/spa/services">
              Explore treatments <span>↘</span>
            </Link>
          </div>
        </div>
        <div className="spa-hero-note">
          <span>01</span>
          <p>Restorative care<br />in the old town</p>
        </div>
      </header>

      <section className="spa-welcome" id="story">
        <div className="wrap">
          <div className="spa-section-label"><span>01</span> Our philosophy</div>
          <div className="spa-welcome-grid">
            <div className="spa-welcome-copy">
              <div className="eyebrow">The wonder of slowing down</div>
              <h2>A calmer rhythm,<br />held with care.</h2>
              <p className="lead">
                Our journey began with a deep passion for wellness and holistic
                healing, shaped by more than a decade of experience in the
                service industry and a heartfelt dedication to therapeutic care.
              </p>
              <p>
                That vision now lives as SAANTI Thai Massage, tucked inside a
                beautifully preserved heritage building in the heart of Song
                Wat Road—a serene retreat where history, healing and heartfelt
                hospitality come together.
              </p>
              <Link className="spa-text-link" to="/spa/story">
                Read our story <span>→</span>
              </Link>
            </div>
            <div className="spa-welcome-images">
              <img className="large" src={image(70)} alt="Sunlit traditional Thai massage room" />
              <img className="small" src={image(9)} alt="SAANTI storefront on Song Wat Road" />
              <div className="spa-stamp">
                <b>SAANTI</b>
                <span>Bangkok · Song Wat</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="spa-section spa-treatments" id="treatments">
        <div className="wrap">
          <div className="spa-section-label light"><span>02</span> Our treatments</div>
          <div className="spa-title-row">
            <div>
              <div className="eyebrow">Highly recommended</div>
              <h2>Choose what your<br />body needs today.</h2>
            </div>
            <p>
              Every price and duration is live from our booking system. Select
              a treatment and we will show you the available times.
            </p>
          </div>

          {(error || branchError) && <div className="spa-error">{error || branchError}</div>}
          {!services && !error && <div className="spa-loading">Loading treatments…</div>}

          <div className="spa-treatment-grid">
            {(services || []).slice(0, 4).map((service, index) => (
              <article className="spa-treatment-card" key={service.id}>
                <div className="spa-treatment-image">
                  <img
                    src={service.image_url || SERVICE_IMAGES[service.code] || image(100 + index)}
                    alt={`${service.name_en} at SAANTI`}
                    loading={index > 1 ? 'lazy' : 'eager'}
                  />
                  <span>{String(index + 1).padStart(2, '0')}</span>
                </div>
                <div className="spa-treatment-body">
                  <div className="spa-treatment-heading">
                    <div>
                      <h3>{service.name_en}</h3>
                      {service.name_th && <div className="th">{service.name_th}</div>}
                    </div>
                    <button aria-label={`Book ${service.name_en}`} onClick={book}>↗</button>
                  </div>
                  <p>{service.description || SERVICE_COPY[service.code] || 'A restorative treatment delivered with care and attention.'}</p>
                  <div className="spa-prices">
                    {(service.variants || []).map((variant) => (
                      <span className="spa-price" key={variant.id}>
                        {variant.durationMinutes} min <b>{money(variant.price)} ฿</b>
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
          <div className="spa-center-action">
            <Link className="spa-cta gold spa-button-link" to="/spa/services">
              View all services
            </Link>
          </div>
        </div>
      </section>

      <section className="spa-section spa-gallery-section" id="gallery">
        <div className="wrap">
          <div className="spa-section-label"><span>03</span> The SAANTI experience</div>
          <div className="spa-title-row dark">
            <div>
              <div className="eyebrow">A quiet room in a loud city</div>
              <h2>Warm light.<br />Unhurried hands.</h2>
            </div>
            <p>
              From the first cup of tea to the final warm towel, every detail is
              designed to let the outside world soften for a while.
            </p>
          </div>
          <div className="spa-gallery">
            {galleryImages.map((src, index) => (
              <figure key={src}>
                <img src={src} alt={`Inside SAANTI Massage ${index + 1}`} loading="lazy" />
              </figure>
            ))}
          </div>
          <div className="spa-center-action">
            <Link className="spa-text-link" to="/spa/facilities">
              Explore our facilities <span>→</span>
            </Link>
          </div>
        </div>
      </section>

      <section className="spa-testimonial" id="guest-words">
        <div className="wrap">
          <div className="spa-section-label light"><span>04</span> Guest impression</div>
          <div className="spa-testimonial-grid">
            <div>
              <div className="eyebrow">Kind words from our guests</div>
              <h2>How SAANTI<br />felt to them.</h2>
            </div>
            <figure>
              <div className="spa-review-quote-stage" aria-live="polite" aria-atomic="true">
                {GUEST_REVIEWS.map((review, index) => {
                  const isCurrent = !reviewTransition && index === reviewIndex;
                  const isLeaving = reviewTransition?.from === index;
                  const isEntering = reviewTransition?.to === index;
                  const directionClass = reviewTransition?.direction === -1 ? ' previous' : ' next';
                  const motionClass = isLeaving
                    ? ` is-visible is-leaving${directionClass}`
                    : isEntering
                      ? ` is-visible is-entering${directionClass}`
                      : isCurrent ? ' is-visible' : '';
                  const thai = reviewIsThai(review);
                  return (
                    <blockquote
                      key={review.author}
                      className={`spa-review-copy ${reviewLengthClass(review)}${
                        thai ? ' thai' : ''}${motionClass}`.trim()}
                      lang={thai ? 'th' : 'en'}
                      aria-hidden={index !== (reviewTransition?.to ?? reviewIndex)}
                    >
                      “{review.quote}”
                    </blockquote>
                  );
                })}
              </div>
              <div className="spa-review-footer">
                <div className="spa-review-author-stage">
                  {GUEST_REVIEWS.map((review, index) => {
                    const isCurrent = !reviewTransition && index === reviewIndex;
                    const isLeaving = reviewTransition?.from === index;
                    const isEntering = reviewTransition?.to === index;
                    const directionClass = reviewTransition?.direction === -1 ? ' previous' : ' next';
                    const motionClass = isLeaving
                      ? ` is-visible is-leaving${directionClass}`
                      : isEntering
                        ? ` is-visible is-entering${directionClass}`
                        : isCurrent ? ' is-visible' : '';
                    return (
                      <figcaption
                        key={review.author}
                        className={`spa-review-author${motionClass}`}
                        aria-hidden={index !== (reviewTransition?.to ?? reviewIndex)}
                      >
                        <span>{review.author}</span>
                        <small>Google Reviews</small>
                      </figcaption>
                    );
                  })}
                </div>
                <div className="spa-review-controls">
                  <button
                    type="button"
                    aria-label="Previous guest review"
                    disabled={Boolean(reviewTransition)}
                    onClick={() => moveReview(-1)}
                  >
                    ←
                  </button>
                  <span>
                    {String((reviewTransition?.to ?? reviewIndex) + 1).padStart(2, '0')}
                    {' '}/ {GUEST_REVIEWS.length}
                  </span>
                  <button
                    type="button"
                    aria-label="Next guest review"
                    disabled={Boolean(reviewTransition)}
                    onClick={() => moveReview(1)}
                  >
                    →
                  </button>
                </div>
              </div>
            </figure>
          </div>
        </div>
      </section>

      <section className="spa-section spa-menu-section">
        <div className="wrap">
          <div className="spa-section-label"><span>05</span> Plan your visit</div>
          <div className="spa-title-row dark">
            <div>
              <div className="eyebrow">Explore before you arrive</div>
              <h2>Our menu &amp;<br />signature packages.</h2>
            </div>
            <p>
              Browse the full treatment menu or discover combinations created
              for a longer, deeper pause.
            </p>
          </div>
          <div className="spa-menu-cards">
            <a href="/spa-images/menu.jpg" target="_blank" rel="noreferrer">
              <img src={image(130)} alt="SAANTI massage menu" loading="lazy" />
              <div><span>Massage menu</span><b>View menu ↗</b></div>
            </a>
            <a href="/spa-images/packages.jpg" target="_blank" rel="noreferrer">
              <img src={image(131)} alt="SAANTI massage packages" loading="lazy" />
              <div><span>Signature packages</span><b>View packages ↗</b></div>
            </a>
          </div>
        </div>
      </section>

      <section className="spa-location" id="locations">
        <img src={image(9)} alt="The entrance to SAANTI Massage on Song Wat Road" loading="lazy" />
        <div className="spa-location-shade" />
        <div className="wrap">
          <div className="spa-location-card">
            <div className="spa-section-label light"><span>06</span> Find us</div>
            {(branches || []).map((branch) => (
              <div key={branch.code}>
                <h2>{branch.name}</h2>
                <div className="th">{branch.nameTh}</div>
                <p>{branch.address || '981 Song Wat Road, Samphanthawong, Bangkok 10100'}</p>
                <dl>
                  <div><dt>Hours</dt><dd>Daily {branch.hours}</dd></div>
                  {branch.phone && (
                    <div>
                      <dt>Telephone</dt>
                      <dd>
                        <a href={`tel:${branch.phone.replace(/[^\d+]/g, '')}`}>{branch.phone}</a>
                      </dd>
                    </div>
                  )}
                </dl>
                <button className="spa-cta light" onClick={() => navigate(`/spa/book?branch=${branch.code}`)}>
                  Book this branch
                </button>
                <Link className="spa-location-link" to="/spa/contact">Contact &amp; directions →</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SpaFooter />
    </div>
  );
}
